import type { Dayjs } from 'dayjs'
import type { Campaign } from 'features/campaigns'

import { contactsPageSize } from 'api/contactsPageSize'
import { isQualification } from 'features/executions/utils'
import { appDay } from 'shared/day'

import {
	AllowedOrder,
	type CampaignKey,
	type CircleKey,
	CIRCLES,
	FallbackOrder,
	type OptionCircle,
	OrderKey,
	type OrderParam,
	Param,
	type SkipKey,
	type SortValuesMode
} from './const'

export type DurationRange = [Dayjs, Dayjs]
export type SimplifiedCampaign = Pick<Campaign.Campaign, 'id' | 'name'>

interface SortState {
	orderBy: OrderKey
	sortBy: string
}

const isDuration = (dates?: Dayjs[]): dates is DurationRange =>
	dates?.length === 2

const durationParser = {
	parse(params: URLSearchParams, key: Param): DurationRange | null {
		const dates = params.get(key)?.split(',').map(appDay)

		return isDuration(dates) ? dates : null
	},

	serialize(range: DurationRange | null, params: URLSearchParams, key: Param) {
		if (range) {
			params.set(key, range.map(day => day.toISOString()).join(','))
		} else {
			params.delete(key)
		}
	}
}

function getOdataArray(values: string[], key: string, quote = false) {
	values = values.map(value => `${key} eq ${quote ? `'${value}'` : value}`)

	return values.length ? `(${values.join(' or ')})` : ''
}

function setArray<I>(params: URLSearchParams, key: Param, values: I[]) {
	if (values.length) {
		params.set(key, values.join(','))
	} else {
		params.delete(key)
	}

	return params
}

function valueToCircle(value: string): null | OptionCircle {
	const number = Number.parseInt(value, 10) - 1

	return CIRCLES[number] ?? null
}

const URLSearchParser = {
	Array(param: URLSearchParams, key: Param) {
		return param.get(key)?.split(',') ?? []
	},

	ArrayToFilter(
		param: URLSearchParams,
		key: Param,
		oDataKey: string,
		quote = false
	) {
		const values = URLSearchParser.Array(param, key)
		return getOdataArray(values, oDataKey, quote)
	},

	Boolean(param: URLSearchParams, key: Param) {
		return param.get(key) === '1'
	},

	Campaigns(
		param: URLSearchParams,
		key: CampaignKey = Param.Campaigns
	): SimplifiedCampaign[] {
		const string = param.get(key)

		return string ? JSON.parse(string) : []
	},

	GotAnser(param: URLSearchParams) {
		const value = URLSearchParser.Integer(param, Param.GotAnswer)

		if (value === 0) {
			return ''
		}
		return `got_answer eq ${value === 1}`
	},

	Integer(param: URLSearchParams, key: string, fallback = 0) {
		const string = param.get(key) ?? ''
		const number = Number.parseInt(string, 10)
		return Number.isNaN(number) ? fallback : number
	},
	LinkedInCircle(
		param: URLSearchParams,
		key: CircleKey = Param.Distance
	): OptionCircle[] {
		const values = URLSearchParser.Array(param, key)

		return values.reduce<OptionCircle[]>((result, string) => {
			const circle = valueToCircle(string)
			circle && result.push(circle)

			return result
		}, [])
	},

	PageSize: (param: URLSearchParams) =>
		URLSearchParser.Integer(param, Param.Take, contactsPageSize.get()),

	QualificationValues(param: URLSearchParams, isModal = false) {
		const values = URLSearchParser.Array(
			param,
			isModal ? Param.ModalLinkedInQualification : Param.LinkedInQualification
		)

		return values.filter(isQualification)
	},

	Skip: (param: URLSearchParams, key: SkipKey = Param.Skip) =>
		URLSearchParser.Integer(param, key),

	Sort(
		param: URLSearchParams,
		key: OrderParam = Param.Order,
		allowedKeys: SortValuesMode = 'General'
	): SortState {
		const value = param.get(key)
		let [sortBy, orderBy]: Array<number | string> = value?.split(' ') ?? []

		orderBy = Number(orderBy) as OrderKey
		const isValidOrder = [OrderKey.Ascending, OrderKey.Descending].includes(
			orderBy
		)
		if (!isValidOrder) {
			orderBy = OrderKey.Ascending
		}

		const isValidSortKey = AllowedOrder[allowedKeys].has(sortBy as any)

		if (!isValidSortKey) {
			sortBy = FallbackOrder[allowedKeys]
		}

		return {
			orderBy,
			sortBy
		} as SortState
	}
}

export {
	durationParser,
	getOdataArray,
	setArray,
	URLSearchParser,
	valueToCircle
}
